/* ============================================================
   MiniFreak Guide — content data for the /minifreak page.
   Distilled from the Arturia MiniFreak User Manual (v1.0.0)
   into beginner-friendly explanations: what each knob does,
   why you'd care, and how to make your first sounds.
   ============================================================ */

/* ---- page sections (drives the nav) ---- */
export const SECTIONS = [
  { id: "big-picture", label: "The Big Picture" },
  { id: "knob-finder", label: "Knob Finder" },
  { id: "oscillators", label: "Oscillators" },
  { id: "filter", label: "Filter" },
  { id: "envelopes", label: "Envelopes" },
  { id: "lfos", label: "LFOs" },
  { id: "matrix", label: "Mod Matrix" },
  { id: "effects", label: "Effects" },
  { id: "performance", label: "Playing It" },
  { id: "arp-seq", label: "Arp & Seq" },
  { id: "presets", label: "Presets" },
  { id: "recipes", label: "First Sounds" },
  { id: "gotchas", label: "Gotchas" },
  { id: "glossary", label: "Glossary" },
];

/* ---- the signal flow, in order ---- */
export const SIGNAL_FLOW = [
  {
    name: "Oscillators",
    role: "Make the raw sound",
    detail:
      "Two digital oscillators generate the raw tone — anything from a classic saw wave to FM bells, plucked strings, or a robot voice.",
  },
  {
    name: "Analog Filter",
    role: "Shape the tone",
    detail:
      "A warm analog filter removes (or emphasizes) frequencies. This is where 'bright vs. dark' and most of the classic synth character happens.",
  },
  {
    name: "VCA + Envelope",
    role: "Shape the volume",
    detail:
      "The amplifier, controlled by the ADSR envelope, gives each note its shape over time — a sharp pluck, a slow swell, a long fade.",
  },
  {
    name: "Digital Effects",
    role: "Add the polish",
    detail:
      "Three FX slots at the end of the chain: chorus, reverb, delay, distortion and more. This is the 'make it sound finished' stage.",
  },
];

export const FLOW_INTRO =
  "Almost every sound on the MiniFreak travels the same road. Modulators (envelopes, LFOs, the mod matrix) don't make sound themselves — they wiggle the knobs along this road for you, automatically.";

/* ---- knob finder: every front-panel control ---- */
export const CONTROLS = [
  // Oscillators
  {
    section: "Oscillators",
    name: "Select (Osc)",
    what: "Picks whether the oscillator knobs edit Osc 1 or Osc 2. Long-press to see the current settings without changing anything.",
  },
  {
    section: "Oscillators",
    name: "Tune / Oct",
    what: "Tunes the selected oscillator up/down in semitones (±48). Hold Shift to jump whole octaves; click the encoder to fine-tune in cents.",
  },
  {
    section: "Oscillators",
    name: "Type (Osc)",
    what: "Chooses the sound engine — the biggest single decision for your sound. Saw waves, FM, plucked strings, speech, noise… each Type is a different instrument.",
  },
  {
    section: "Oscillators",
    name: "Wave",
    what: "Does something different for every oscillator Type — usually the main 'flavor' control. The screen shows what it means for the current Type.",
  },
  {
    section: "Oscillators",
    name: "Timbre",
    what: "Second character control; changes meaning per Type (detune, formant, FM amount…). Watch the display and trust your ears.",
  },
  {
    section: "Oscillators",
    name: "Shape",
    what: "Third character control, also per-Type (sub-oscillator level, decay, feedback…). Wave/Timbre/Shape are the heart of the MiniFreak — twist them freely.",
  },
  {
    section: "Oscillators",
    name: "Volume (Osc)",
    what: "Level of the selected oscillator. Shift+turn sets the mix between Osc 1 and 2. When Osc 2 is processing Osc 1, this becomes a Dry/Wet control.",
  },
  // Filter
  {
    section: "Filter",
    name: "Type (Filter)",
    what: "Picks the filter mode: Low Pass (cuts highs — the classic), Band Pass (keeps a middle band), or High Pass (cuts lows — thin, airy).",
  },
  {
    section: "Filter",
    name: "Cutoff",
    what: "THE synth knob. Where the filter starts cutting. Turn it down for dark and muffled, up for bright and open. Sweep it while a note plays and you'll get it instantly.",
  },
  {
    section: "Filter",
    name: "Resonance",
    what: "Boosts the frequencies right at the cutoff point, adding a sharp, whistly, aggressive peak. All the way up, the filter sings by itself.",
  },
  {
    section: "Filter",
    name: "Env / Velo",
    what: "How much the envelope pushes the cutoff around (center = off; it can go negative). Shift+turn sets how much key velocity affects that — play harder, sound brighter.",
  },
  // FX
  {
    section: "Effects",
    name: "Select (FX)",
    what: "Picks which of the three FX slots (FX1/2/3) the knobs edit. Bright LED = editing it, dim = on but not selected, off = off.",
  },
  {
    section: "Effects",
    name: "Type / Sub",
    what: "Chooses the effect for the slot, and Shift picks a Subtype (a preset flavor of that effect). Click the encoder to turn the effect on or off.",
  },
  {
    section: "Effects",
    name: "Time / Intensity / Amount",
    what: "Three FX knobs whose meaning changes per effect (rate/depth/mix for chorus, time/feedback/mix for delay…). The display shows what they are.",
  },
  {
    section: "Effects",
    name: "Master",
    what: "Overall output volume for the main outs and headphones. Not saved with presets — it's just 'how loud is the synth right now.'",
  },
  // General
  {
    section: "Playing It",
    name: "Shift",
    what: "Hold it to reach every function printed in blue on the panel. Half the MiniFreak lives behind this button.",
  },
  {
    section: "Playing It",
    name: "Octave buttons",
    what: "Shift the 37-key keyboard up or down, up to 3 octaves each way. Press both together to reset.",
  },
  {
    section: "Playing It",
    name: "Hold / Tie",
    what: "Sustains whatever you play (like a sustain pedal you don't have to hold). In the sequencer, it enters a rest or ties notes across steps.",
  },
  {
    section: "Playing It",
    name: "Chord / Scale",
    what: "Chord mode: one key plays a whole chord you've programmed. Shift+press opens Scale setup, so wrong notes become impossible.",
  },
  {
    section: "Playing It",
    name: "Glide",
    what: "Portamento — how long the pitch takes to slide from one note to the next. A little = smooth and vocal; a lot = sci-fi swoops.",
  },
  // LFO
  {
    section: "LFOs",
    name: "Select / Edit (LFO)",
    what: "Picks LFO 1 or LFO 2 for editing. Shift+click opens the Shaper, where you can draw your own LFO waveform in up to 16 steps.",
  },
  {
    section: "LFOs",
    name: "Rate / Trig",
    what: "LFO speed, from one cycle per minute to audio-rate. Click to sync it to the tempo (musical divisions); Shift+turn picks how it restarts (per note, free-running…).",
  },
  {
    section: "LFOs",
    name: "Wave / Load (LFO)",
    what: "Picks the LFO shape: sine (smooth), square (on/off), saw, random (S&H), and more. Shift+turn loads drawn Shaper waves from the library.",
  },
  // Display area
  {
    section: "Presets",
    name: "Preset / Edit encoder",
    what: "Turn to browse presets, click to load. Also the all-purpose menu knob. Shift+turn filters presets by type (Bass, Pad, Keys…). Long-press = fresh init preset.",
  },
  {
    section: "Presets",
    name: "Save",
    what: "Saves the current sound. Click once, then click again to confirm slot/type/name steps. Careful: no 'are you sure?' when overwriting.",
  },
  {
    section: "Presets",
    name: "Panel",
    what: "(Shift+Save) Snaps every parameter to where the physical knobs actually point. Great right after loading a preset, destructive after you've been editing — it can't be undone.",
  },
  {
    section: "Presets",
    name: "Sound Edit / Utility",
    what: "Sound Edit (white) = deeper per-preset settings menu (velocity response, glide mode, unison…). Shift makes it Utility (blue) = global machine settings.",
  },
  // Matrix
  {
    section: "Mod Matrix",
    name: "Matrix encoder",
    what: "Turn to pick a source→destination crossing point in the grid, click to set the modulation amount. Lit LED = that connection is active. Hold 1 second to zero it out.",
  },
  // Cycling env
  {
    section: "Envelopes",
    name: "Mode (CycEnv)",
    what: "Sets the Cycling Envelope's personality: Env = a second one-shot envelope; Run = free-running for all voices; Loop = repeats per note like a shapeable LFO.",
  },
  {
    section: "Envelopes",
    name: "Rise / Fall / Hold-Sustain",
    what: "The Cycling Envelope's three stages: time to go up, time to come down, and how long it holds (or sustains, in Env mode). Shift+turn Rise/Fall bends the curve shape.",
  },
  // Envelope
  {
    section: "Envelopes",
    name: "Attack",
    what: "How long a note takes to reach full volume. 0 = instant pluck/hit; high = slow swell like bowed strings.",
  },
  {
    section: "Envelopes",
    name: "Decay",
    what: "After the attack peak, how long the note takes to fall to the sustain level. Short decay + low sustain = percussive and plucky.",
  },
  {
    section: "Envelopes",
    name: "Sustain",
    what: "A LEVEL, not a time: the volume a held note settles at. Full = organ-like; zero = the note dies away even if you keep holding the key.",
  },
  {
    section: "Envelopes",
    name: "Release",
    what: "How long sound lingers after you let go of the key. Short = tight; long = a soft tail that blurs into the next note.",
  },
  {
    section: "Envelopes",
    name: "Mode (Voices)",
    what: "How notes use the 6 voices: Mono (one note at a time), Poly (chords), Para (12 thinner voices), Unison (many voices stacked on one note = huge).",
  },
  // Touch strips
  {
    section: "Playing It",
    name: "Touch Strips",
    what: "Two touch strips with three personalities: Bend + Mod wheel (white), Macros M1/M2 (blue), or Gate + Spice for the arp/sequencer (orange). The pads above pick the mode.",
  },
  {
    section: "Playing It",
    name: "Bend strip",
    what: "The left strip in white mode: bends pitch and snaps back to center when you let go. Range is 1–12 semitones, set in Sound Edit > Keyboard > Bend Range.",
  },
  {
    section: "Playing It",
    name: "Mod Wheel strip",
    what: "The right strip in white mode. It's a modulation SOURCE — it does nothing until you route it in the Matrix (bottom row, 'Wheel'). It stays where you leave it. Shift+touch the pad to turn it into Vibrato instead.",
  },
  {
    section: "Playing It",
    name: "Macros (M1 / M2)",
    what: "One swipe moves up to four knobs at once, by amounts you choose. Set up with Shift+touch on the Macro pad, then grab knobs. Perfect for performance sweeps.",
  },
  // Arp/seq
  {
    section: "Arp & Seq",
    name: "Arp / Seq buttons",
    what: "Turn on the arpeggiator (auto-plays held notes as patterns) or the sequencer (records up to 64 steps, one sequence saved per preset).",
  },
  {
    section: "Arp & Seq",
    name: "Tempo / Swing",
    what: "Sets the tempo (30–240 BPM, saved per preset). Click to set the time division (1/8, 1/16…); Shift+turn adds swing (50–75%) for groove.",
  },
  {
    section: "Arp & Seq",
    name: "Spice & Dice",
    what: "Controlled chaos: Dice rolls random variations (velocity, octave, gate, mutes) and Spice decides how much of it you hear. Non-destructive — your sequence is safe.",
  },
];

/* ---- oscillator engine guide ---- */
export const OSC_TYPES = [
  {
    name: "BasicWaves",
    avail: "both",
    vibe: "Classic analog synth — start here",
    desc: "Morphs between square and saw waves, the two workhorse shapes of vintage synths. Simple, rich, and perfect for learning what the filter and envelopes do.",
    knobs: {
      wave: "Morph: square (0) → saw (50) → double saw an octave up (100)",
      timbre: "Symmetry: pulse width below 50, saw phasing above (LFO here = classic PWM)",
      shape: "Sub: adds a sine one octave down for low-end weight",
    },
  },
  {
    name: "SuperWave",
    avail: "both",
    vibe: "Huge, detuned, trance-y",
    desc: "Stacks detuned copies of a wave on top of itself — the famous 'supersaw' sound. Instant big lead or pad.",
    knobs: {
      wave: "Base wave: saw, square, triangle, or sine",
      timbre: "Detune between the copies, subtle to seasick",
      shape: "Volume of the copies vs. the original",
    },
  },
  {
    name: "Harmo",
    avail: "both",
    vibe: "Glassy, organ-ish, evolving",
    desc: "Additive synthesis: builds sound from up to 8 harmonics using preset harmonic 'tables.' Good for shimmering keys and organs.",
    knobs: {
      wave: "Which table of harmonics (higher = more of them)",
      timbre: "Morphs harmonics from sine toward triangle",
      shape: "Built-in chorus to widen and thicken",
    },
  },
  {
    name: "KarplusStr",
    avail: "both",
    vibe: "Plucked strings & struck drums",
    desc: "Physical modeling of a plucked/bowed string or drumhead — a burst of noise ringing through a tuned delay. Very organic.",
    knobs: {
      wave: "Bow: continuous bowed tone vs. decaying pluck",
      timbre: "Position: where the 'drumhead' is struck",
      shape: "Decay: how fast the ringing dies away",
    },
  },
  {
    name: "VAnalog",
    avail: "both",
    vibe: "Two virtual analog waves, detunable",
    desc: "A pulse wave plus a sawtooth, each with variable shape, with detune between them. Classic analog moves including sync-like sounds.",
    knobs: {
      wave: "Saw shape: triangle → bright saw",
      timbre: "Detune of saw vs. pulse, up to ±2 octaves",
      shape: "Pulse shape: narrow pulse → square → sync-style scream",
    },
  },
  {
    name: "Waveshaper",
    avail: "both",
    vibe: "Twisted, folded, gnarly",
    desc: "Bends and folds the waveform back on itself (à la Buchla) for aggressive, complex timbres. All three knobs make big changes — great for modulation.",
    knobs: {
      wave: "Waveform coming out of the shaper",
      timbre: "Amount of wavefolding",
      shape: "Asymmetry of the wave",
    },
  },
  {
    name: "Two Op. FM",
    avail: "both",
    vibe: "Bells, electric pianos, clangs",
    desc: "Frequency modulation, DX7-style but with 2 operators. Clean ratios sound harmonic (organs, e-pianos); weird ratios go bell-like and metallic.",
    knobs: {
      wave: "Ratio between the two operators (2:1, 1.5:1… or in-between chaos)",
      timbre: "FM amount: more = brighter and busier",
      shape: "Feedback: the modulator eats its own tail for extra edge",
    },
  },
  {
    name: "Formant",
    avail: "both",
    vibe: "Vowel-ish, vocal, talky",
    desc: "Granular formant synthesis — resonances that stay fixed like a human vocal tract, so it sounds voice-like at any pitch.",
    knobs: {
      wave: "Interval between the two formant peaks",
      timbre: "Base formant frequency ('who is talking')",
      shape: "Formant width and shape",
    },
  },
  {
    name: "Speech",
    avail: "both",
    vibe: "Speak & Spell robot voice",
    desc: "Actual synthesized speech: vowels, colors, numbers, letters, even synth words. Silly and wonderful.",
    knobs: {
      wave: "Which library (formants, colors, numbers, alphabet…)",
      timbre: "Shifts formants up/down — chipmunk to giant",
      shape: "Which word/sound within the library",
    },
  },
  {
    name: "Modal",
    avail: "both",
    vibe: "Mallets, bells, xylophones",
    desc: "Physical modeling of struck resonant objects. From woody marimba to church bell with a couple of twists.",
    knobs: {
      wave: "Inharmonicity: woody → metallic",
      timbre: "Exciter brightness (soft mallet vs. hard stick)",
      shape: "Decay: how fast the resonance damps",
    },
  },
  {
    name: "Noise",
    avail: "both",
    vibe: "Wind, hiss, grit, percussion",
    desc: "Noise in many flavors, mixable with a basic tone wave so one oscillator gives you both pitch and air.",
    knobs: {
      wave: "Noise color: particle → white → metallic",
      timbre: "Sample-rate crunch (or metallic pitch)",
      shape: "Blend: pure noise → noise + sine/triangle/square",
    },
  },
  {
    name: "Bass",
    avail: "both",
    vibe: "Deep, saturated low-end",
    desc: "Noise Engineering's bass engine: sine/cosine through modeled circuits with saturation and folding. Purpose-built thump.",
    knobs: {
      wave: "Saturation — adds harmonics to the pure tone",
      timbre: "Wavefolding for more bite",
      shape: "Noise injected for grit",
    },
  },
  {
    name: "SawX",
    avail: "both",
    vibe: "Modern aggressive saw",
    desc: "A sawtooth phase-modulated with crunched noise plus chorus — rich, wide, and modern-sounding.",
    knobs: {
      wave: "Modulation intensity (big harmonic changes)",
      timbre: "Chorus amount — sweep it slowly for lushness",
      shape: "Noise modulation, up to faint radio static",
    },
  },
  {
    name: "Harm",
    avail: "both",
    vibe: "Harmonics with a twist",
    desc: "Noise Engineering's take on additive harmonics with rectification — from smooth unison to ring-mod-like weirdness.",
    knobs: {
      wave: "Spread of harmonics vs. the fundamental",
      timbre: "Rectification (wavefolder-ish edge)",
      shape: "Phase-modulated noise and clipping",
    },
  },
  {
    name: "Audio In",
    avail: "osc1",
    vibe: "Run other gear through it",
    desc: "Osc 1 only: pipe any audio (drum machine, vocals, your phone) into the MiniFreak's filter, FX and modulation. Hold a key to open the amp.",
    knobs: {
      wave: "Wavefold depth (acts as input level when Decimate = 0)",
      timbre: "Decimation crunch",
      shape: "Added noise",
    },
  },
  {
    name: "Chords",
    avail: "osc2",
    vibe: "One finger = full chord",
    desc: "Osc 2 only: every note becomes a chord — minor 9ths, sus4, major 7s and more, with inversion control. Instant lo-fi beauty.",
    knobs: {
      wave: "Chord type (octave, 5th, m7, M9…)",
      timbre: "Inversion / transposition of the voicing",
      shape: "Waveform, morphing through a 16-wave wavetable up top",
    },
  },
  {
    name: "FM / RM",
    avail: "osc2",
    vibe: "Osc 1 mangles Osc 2",
    desc: "Osc 2 only: Osc 1 frequency-modulates and/or ring-modulates Osc 2 for hollow, metallic, inharmonic tones.",
    knobs: {
      wave: "Osc 2's waveform (the one being modulated)",
      timbre: "FM amount",
      shape: "Ring mod amount",
    },
  },
  {
    name: "Multi Filter",
    avail: "proc",
    vibe: "Extra filters (processes Osc 1)",
    desc: "Turns Osc 2 into a second, digital filter for Osc 1: notch, band pass, and slopes from gentle 6 to steep 36 dB/oct.",
    knobs: {
      wave: "Cutoff",
      timbre: "Resonance",
      shape: "Filter type + slope (LP12…36, BP, Notch…)",
    },
  },
  {
    name: "Surgeon Filter",
    avail: "proc",
    vibe: "Precision EQ scalpel (processes Osc 1)",
    desc: "A parametric-EQ-like filter for surgically boosting or cutting one region of Osc 1's sound.",
    knobs: {
      wave: "Center frequency",
      timbre: "Width of the peak/cut (BP & Notch modes)",
      shape: "Mode: LP, BP, HP, or Notch",
    },
  },
  {
    name: "Comb Filter",
    avail: "proc",
    vibe: "Hollow, metallic, flanger-ish (processes Osc 1)",
    desc: "A short delay creating evenly spaced notches — the hollow 'comb' sound. Modulate it for flanging.",
    knobs: {
      wave: "Delay time / frequency balance (tracks the keyboard)",
      timbre: "Amount of delayed signal (0 = no effect)",
      shape: "Damping — softens the feedback",
    },
  },
  {
    name: "Phaser Filter",
    avail: "proc",
    vibe: "Swooshy notches (processes Osc 1)",
    desc: "All-pass filter stages create unevenly spaced notches for that classic phaser swirl, as part of the voice itself.",
    knobs: {
      wave: "Frequency range of the notches",
      timbre: "Feedback — more intensity",
      shape: "Poles: more poles = more notches",
    },
  },
  {
    name: "Destroy",
    avail: "proc",
    vibe: "Total destruction (processes Osc 1)",
    desc: "Wavefolder + decimator + bitcrusher aimed at Osc 1. From gentle crunch to digital rubble.",
    knobs: {
      wave: "Wavefold amount",
      timbre: "Decimation (throws samples away)",
      shape: "Bitcrush depth",
    },
  },
];

export const OSC_AVAIL_LABELS = {
  both: "Osc 1 & 2",
  osc1: "Osc 1 only",
  osc2: "Osc 2 only",
  proc: "Osc 2 · processes Osc 1",
};

/* ---- effects guide ---- */
export const FX_TYPES = [
  {
    name: "Chorus",
    use: "Width & shimmer",
    desc: "Detuned copies of the sound layered on top — makes anything wider, softer, and more expensive-sounding. A go-to for pads and keys.",
    knobs: "Rate (wobble speed) · Depth (intensity) · Dry/Wet",
  },
  {
    name: "Phaser",
    use: "Slow swirling movement",
    desc: "Sweeping frequency notches for that swooshy, underwater movement. More feedback = thicker, more resonant swirl.",
    knobs: "Rate · Feedback · Dry/Wet",
  },
  {
    name: "Flanger",
    use: "Jet-engine intensity",
    desc: "Like a chorus with a much shorter delay — a sharper, more metallic sweep. High feedback gets close to filter-resonance territory.",
    knobs: "Rate · Feedback · Dry/Wet",
  },
  {
    name: "Reverb",
    use: "Space & atmosphere",
    desc: "Puts the sound in a room — or a cathedral. Damping controls how quickly the sparkle fades. Only one reverb allowed in the chain.",
    knobs: "Decay (room size) · Damping · Dry/Wet or Send",
  },
  {
    name: "Delay",
    use: "Echoes & rhythm",
    desc: "Repeats the sound. Sync subtypes lock repeats to the tempo; feedback sets how many echoes. Ping-pong bounces left/right. One per chain.",
    knobs: "Time · Feedback · Dry/Wet or Send",
  },
  {
    name: "Distortion",
    use: "Warmth to mayhem",
    desc: "Six flavors from soft tape saturation to full clipping chaos. Gain drives the character; the tone knob is a low-pass/high-pass in one.",
    knobs: "Gain · HPF/LPF tone · Dry/Wet",
  },
  {
    name: "Bit Crusher",
    use: "Lo-fi digital grit",
    desc: "Reduces bit depth and throws samples away — from 'vintage sampler' to 'broken toy.' Wonderful on plucks and drums.",
    knobs: "Decimate · BitDepth · Dry/Wet",
  },
  {
    name: "3 Bands EQ",
    use: "Quick tone control",
    desc: "Simple mixer-style Low/Mid/High gain knobs to tilt the overall tone of the preset.",
    knobs: "Low · Mid · High (−15 to +6 dB each)",
  },
  {
    name: "Peak EQ",
    use: "Boost or cut one spot",
    desc: "One fully controllable EQ band — cut an annoying resonance or boost a sweet spot.",
    knobs: "Frequency · Gain · Width",
  },
  {
    name: "Multi Comp",
    use: "Glue & punch",
    desc: "A multiband compressor with the hard parts pre-cooked into subtypes. Evens out levels and adds punch; let your ears judge. One per chain.",
    knobs: "Time (attack/release) · Tone · Amount",
  },
];

export const FX_ROUTING_NOTE =
  "The three slots always run in order: FX1 → FX2 → FX3, like pedals in a row. Delay and Reverb can each be flipped from Insert (in the chain) to Send (parallel, mixed in at the end) via Sound Edit > FX — Send keeps echoes and reverb from being re-processed by everything after them.";

/* ---- arpeggiator ---- */
export const ARP_MODES = [
  {
    name: "Up",
    desc: "Plays your held notes lowest to highest, over and over. The classic.",
  },
  {
    name: "Down",
    desc: "Highest to lowest. Instantly moodier.",
  },
  {
    name: "UpDown",
    desc: "Climbs up, then walks back down — a smooth back-and-forth wave.",
  },
  {
    name: "Random",
    desc: "Picks freely among your held notes. Sparkly and unpredictable.",
  },
  {
    name: "Order",
    desc: "Plays notes in the order you pressed them — so the order of your fingers writes the melody.",
  },
  {
    name: "Walk",
    desc: "A drunken stroll through your notes: usually steps forward (50%), sometimes lingers or steps back (25% each). Organic, never quite repeating.",
  },
  {
    name: "Poly",
    desc: "Pulses the entire chord as one repeated stab instead of separating the notes. Instant dance-chord energy.",
  },
  {
    name: "Pattern",
    desc: "Generates a random melody from your held notes, favoring the lowest note as the root (it appears twice as often). Each new key press rolls a new pattern; set its length with Last Step.",
  },
];

export const ARP_MODIFIERS = [
  {
    name: "Repeat",
    hold: true,
    desc: "Every note plays twice in a row while you hold the pad — an instant stutter.",
  },
  {
    name: "Ratchet",
    hold: true,
    desc: "Doubles the triggers while held: each step becomes a rapid double-hit, like a drum roll. Great for fills.",
  },
  {
    name: "Rand Oct",
    hold: false,
    desc: "A toggle that sprinkles octave jumps: 75% normal, 15% an octave up, 7% down, 3% two octaves up. Turns a static arp into something alive.",
  },
  {
    name: "Mutate",
    hold: true,
    desc: "Each pass, notes can drift: mostly they stay (75%), sometimes jump a fifth up, a fourth down, an octave either way, or swap places with a neighbor. Mutations stack on each other — release and re-press a key to reset its note.",
  },
];

/* ---- sequencer ---- */
export const SEQ_STEP_RECORDING = [
  "Press Seq, stay in Stop, then press Record — the display shows the current step, and you're in step mode.",
  "Play a note or chord (up to 6 notes fit in a step). When you release all keys, the sequencer moves to the next step.",
  "Want a rest? Press Hold/Tie with no keys held — the step stays silent and you move on.",
  "Want a long note? Hold keys and tap Hold/Tie once per extra step to stretch them across steps.",
  "Tap any step pad to jump straight there and fix just that step. Press Record again when you're done.",
];

export const SEQ_EDIT_TRICKS = [
  {
    name: "Mute / unmute a step",
    desc: "Tap a lit step to silence it — its notes are kept and come back when you tap again. Tapping an empty step drops in a default C3.",
  },
  {
    name: "Tweak one step's feel",
    desc: "Hold a step down: the Preset encoder cycles through its notes, and the two touch strips become note-length and velocity sliders for what's selected.",
  },
  {
    name: "Copy / paste / erase",
    desc: "Hold the Off/Seq/Arp pad and tap steps (or a Page button) to copy, paste, or erase them — handy for building variations fast.",
  },
  {
    name: "Double the sequence",
    desc: "Shift + a Page button copies your pattern out to that page — turn 16 steps into 32 or 64, then edit the copies for variation.",
  },
  {
    name: "Overdub on/off",
    desc: "Shift+Record toggles it. Red Record LED = new notes replace what's there; blue = they're layered on top of the existing steps.",
  },
  {
    name: "Transpose live",
    desc: "While the sequence plays, Shift + any key shifts the whole pattern to that root (not saved). From Stop, just playing a key starts the sequence transposed — release to stop.",
  },
];

export const GROOVE_TOOLS = [
  {
    name: "Tempo",
    desc: "30–240 BPM, saved with each preset — recall a sequence and its groove comes with it.",
  },
  {
    name: "Time division",
    desc: "Click the Tempo encoder: 1/2 down to 1/32, with dotted and triplet flavors. Hold the encoder while browsing to stay within the same family (all triplets, say).",
  },
  {
    name: "Swing",
    desc: "Shift+turn Tempo: 50% is robot-straight, 75% is a heavy shuffle. Around 54–62% is where grooves start to feel human. Playback-only — your recorded notes don't move.",
  },
  {
    name: "Gate",
    desc: "Orange strip mode, left strip: how long each step sounds, 1–99% of the division. Low = tight staccato blips, high = notes that melt together.",
  },
  {
    name: "Spice & Dice",
    desc: "Shift+touch the orange pad to roll the Dice — a fresh random variation of velocity, octaves, gate lengths, mutes, and envelope times. The Spice strip sets how much of it you hear, 0 to full chaos. Never touches the stored sequence.",
  },
  {
    name: "Metronome",
    desc: "Shift+click the Tempo encoder for a click track while recording; its volume lives in the Utility menu.",
  },
];

export const SEQ_MOD_LANES_NOTE =
  "Press the Mods button and the sequencer grows four extra lanes that record knob movements — cutoff, Wave/Timbre/Shape, FX knobs, envelope times, even the Macros. In step mode, hold a step and turn a knob to set that step's offset; in real-time record, just perform the knob moves while it loops (it records exactly one pass, then drops out of record). Transitions between steps are smoothed by default — turn that off per lane in Sound Edit > Seq for stepped, robotic jumps.";

/* ---- recipes: first sounds ---- */
export const RECIPES = [
  {
    name: "Fat mono bass",
    vibe: "Round, punchy low-end",
    steps: [
      "Long-press the Preset encoder to start from a fresh init sound.",
      "Osc 1 Type: BasicWaves. Wave at 0 (square) or 50 (saw). Turn Shape up halfway — that's the sub-oscillator adding weight.",
      "Filter: Low Pass. Bring Cutoff down until it's dark but still has some bite; add a little Resonance.",
      "Envelope: Attack 0, Decay around 1/3, Sustain low-ish, Release short. Now notes 'thump' instead of drone.",
      "Turn the filter's Env/Velo knob up a bit — the envelope now opens the filter on each note for extra punch.",
      "Voice Mode: press Mode until Mono (or try Uni for enormous). Add a touch of Glide for slides between notes.",
    ],
  },
  {
    name: "Dreamy pad",
    vibe: "Slow, wide, cinematic",
    steps: [
      "Init preset, then Osc 1 Type: SuperWave with Wave on saw. Turn Timbre (detune) up to taste — it gets lush fast.",
      "Envelope: slow Attack (1–2 o'clock), full Sustain, long Release. Notes now swell in and melt away.",
      "Filter: Low Pass, Cutoff around halfway so it's warm, not buzzy.",
      "Matrix: route LFO1 → Cutoff with a small amount. Set LFO1 to a slow sine. The sound now breathes on its own.",
      "FX: slot 1 Chorus, slot 2 Reverb with long decay. Hold a chord and enjoy.",
    ],
  },
  {
    name: "Plucky arp thing",
    vibe: "Tight, bouncy, sequenced",
    steps: [
      "Init preset. Osc 1 Type: KarplusStr (a real pluck model) — or BasicWaves with a snappy envelope.",
      "Envelope: Attack 0, Decay short, Sustain 0, Release short. Every note is a little 'plink.'",
      "Env/Velo knob up so the filter snaps open on each hit; Cutoff fairly low.",
      "Press Arp, hold a chord, and try the Up / Order / Pattern modes. Click the Tempo encoder to change the speed division.",
      "Touch the Gate/Spice pad (orange): left strip shortens/lengthens notes, right strip adds Spice. Shift+touch rolls the Dice for variations.",
      "FX: a synced Delay (subtype with 'Sync') plus a small Reverb turns three held notes into a track.",
    ],
  },
  {
    name: "One-finger chord magic",
    vibe: "Instant lo-fi beauty",
    steps: [
      "Init preset. Select Osc 2, set its Type to Chords, and turn Osc 1's Volume down (or blend them).",
      "Wave picks the chord type — try m9 or M9 for instant tasteful jazz. Shape picks the waveform.",
      "Shift+press Chord/Scale to set a Scale (e.g. Minor) so every key you press is in key.",
      "Slowish attack, medium release, Chorus + Reverb. Play single notes — get whole progressions.",
    ],
  },
  {
    name: "Robot friend",
    vibe: "Speak & Spell nonsense",
    steps: [
      "Init preset. Osc 1 Type: Speech. Turn Wave up high to reach the word libraries.",
      "Play a key and turn Shape — it steps through actual words at the pitch you're playing.",
      "Timbre shifts the voice from giant to chipmunk.",
      "Add Bit Crusher and a synced Delay. Congratulations, you have a robot bandmate.",
    ],
  },
];

/* ---- gotchas ---- */
export const GOTCHAS = [
  {
    title: "The Panel button erases your edits",
    body: "Panel (Shift+Save) snaps everything to the physical knob positions. Use it right AFTER loading a preset, never after editing — there's no undo.",
  },
  {
    title: "Save doesn't ask 'are you sure?'",
    body: "When the Save button is lit, pressing it again saves immediately to the shown slot. Double-check the slot number before the second press.",
  },
  {
    title: "The knobs may not match the sound",
    body: "Analog-style knobs (Cutoff, Resonance, ADSR…) stay wherever they physically point when you load a preset — the sound uses the saved values until you move a knob. That's normal; the Panel button exists for exactly this.",
  },
  {
    title: "Still hear modulation with an empty matrix?",
    body: "Not all modulation lives in the Matrix. The filter's Env/Velo knob and the Macros are separate — check those when a sound moves and you don't know why.",
  },
  {
    title: "Osc 2 sometimes changes the rules",
    body: "When Osc 2 runs a processor Type (Multi Filter, Destroy…), Osc 1's Volume feeds INTO Osc 2 and Osc 2's Volume becomes a Dry/Wet mix. In Paraphonic mode, Osc 2 turns off entirely.",
  },
  {
    title: "Everything blue needs Shift",
    body: "Any label printed in blue on the panel is a Shift function. If you can't find a feature, hold Shift and look again.",
  },
  {
    title: "Lost? Init and start fresh",
    body: "Long-press the Preset encoder for a clean init patch, or use Utility > Preset Operations > Preset Erase to clear just the Matrix, sequence, or Macros. Sound Edit > View Snapshots can rescue you from a bad edit, too.",
  },
  {
    title: "The self-oscillating filter trick",
    body: "Resonance all the way up + oscillator volumes at 0 = the filter whistles a sine wave on its own. Route Keyboard → Cutoff at 50 in the Matrix and it plays in tune with the keys.",
  },
];

/* ---- glossary ---- */
export const GLOSSARY = [
  ["Oscillator", "The part that makes raw sound — a tone generator. The MiniFreak has two digital ones."],
  ["Waveform", "The shape of the oscillation, which sets the raw character: sine (pure), triangle (soft), saw (bright/buzzy), square (hollow)."],
  ["Subtractive synthesis", "Start with a bright, rich sound and carve away frequencies with a filter. The classic synth recipe, and the MiniFreak's core flow."],
  ["Filter", "Removes frequencies. Low Pass keeps lows, High Pass keeps highs, Band Pass keeps a middle slice."],
  ["Cutoff", "The frequency where the filter starts working. The most important knob on the synth."],
  ["Resonance", "A boost right at the cutoff, adding a whistly, aggressive peak. Max it and the filter sings by itself."],
  ["Envelope", "A shape applied to something over the life of one note — usually volume (ADSR) or filter brightness."],
  ["ADSR", "Attack, Decay, Sustain, Release — how fast a note starts, falls, where it settles, and how long it lingers after release."],
  ["LFO", "Low Frequency Oscillator — a slow, inaudible wave used to wiggle other knobs automatically (vibrato, wobble, sweeps)."],
  ["Modulation", "Any 'something controls something else' — an LFO moving the cutoff, velocity changing brightness, etc."],
  ["Mod Matrix", "The grid where you connect modulation sources (LFOs, envelopes, wheel…) to destinations (cutoff, pitch, FX…) with an amount."],
  ["VCA", "The amplifier at the end of the voice; the main envelope shapes its volume on every note."],
  ["Polyphony", "How many notes can sound at once. The MiniFreak has 6 voices (12 in Paraphonic mode)."],
  ["Mono / Unison", "Mono = one note at a time. Unison = several voices stacked and detuned on one note for a huge sound."],
  ["Paraphonic", "A special 12-voice mode: more notes, but they share parts of the signal path (and Osc 2 turns off)."],
  ["Glide (portamento)", "Pitch slides between notes instead of jumping."],
  ["PWM", "Pulse Width Modulation — wiggling a square wave's shape with an LFO for a classic thick, chorus-y tone."],
  ["FM", "Frequency Modulation — one oscillator wobbles another's pitch at audio speed, creating bells and metallic tones."],
  ["Sub oscillator", "A tone one octave below the main one, added for low-end weight."],
  ["Detune", "Slightly offsetting pitches against each other. A little = warm and wide; a lot = out of tune (or awesome)."],
  ["Dry / Wet", "Mix between the unprocessed signal (dry) and the effected one (wet)."],
  ["Arpeggiator", "Auto-plays the notes you hold as a pattern (up, down, random…) in time with the tempo."],
  ["Sequencer", "Records a loop of notes (up to 64 steps here) plus knob movements, saved with the preset."],
  ["Velocity", "How fast/hard you hit a key. Often mapped to volume and brightness."],
  ["Aftertouch", "Pressing harder on a key you're already holding — an extra modulation source under your fingers."],
  ["Sample & Hold (S&H)", "An LFO shape that jumps to random values — the classic 'computer burbling' modulation."],
  ["Sync (tempo)", "Locks an LFO, delay, or arp rate to the tempo in musical divisions (1/4, 1/8, 1/16…)."],
  ["Key tracking", "Higher notes open the filter more, like acoustic instruments getting brighter as they go up."],
  ["Macro", "One control that moves several parameters at once, by amounts you design. The MiniFreak has two, on the touch strips."],
];
